import type {JSX, ReactNode} from 'react';
import {useMemo, useState} from 'react';

import {appPaths} from '@/config/appPaths';
import {transactionService} from '@/services/transactionService';
import type {AfriexAdminTransaction} from '@/types';
import {AfriexTransactionStatus} from '@/types';
import {AfriexTransactionType} from '@/types/Transaction';
import {getResponseMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

import {TransactionContext} from './TransactionContext';

export default function TransactionProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [totalPendingTransactions, setTotalPendingTransactions] = useState<
    number | null
  >(null);
  const [totalFailedTransactions, setTotalFailedTransactions] = useState<
    number | null
  >(null);

  // Text Added After a menu Label
  const [menuSuffixes, setMenuSuffixes] = useState<Record<string, string>>({});

  const handleSetTotalPendingTransactions = (total: number) => {
    setTotalPendingTransactions(total);
    // update pending Transactions with the total Number of Pending Transactions
    setMenuSuffixes({
      ...menuSuffixes,
      [`${appPaths.transactions}/pending`]: `(${total})`,
    });
  };

  const handleSetTotalFailedTransactions = (total: number) => {
    setTotalFailedTransactions(total);
    // update pending Transactions with the total Number of Pending Transactions
    setMenuSuffixes({
      ...menuSuffixes,
      [`${appPaths.transactions}/failed`]: `(${total})`,
    });
  };

  async function fetchPendingTransactions(
    limit = 10,
    page = 0,
  ): Promise<AfriexAdminTransaction[] | undefined> {
    try {
      const response = await transactionService.getTransactionList({
        status: AfriexTransactionStatus.PENDING,
        type: AfriexTransactionType.WITHDRAW,
        limit,
        page,
      });
      handleSetTotalPendingTransactions(response.total);
      return response.data;
    } catch (err) {
      showErrorMessage(
        getResponseMessage(err) || 'Failed to fetch pending transactions',
      );
    }
  }

  async function fetchFailedTransactions(
    limit = 10,
    page = 0,
  ): Promise<AfriexAdminTransaction[] | undefined> {
    try {
      const response = await transactionService.getTransactionList({
        status: AfriexTransactionStatus.FAILED,
        type: AfriexTransactionType.WITHDRAW,
        limit,
        page,
      });
      handleSetTotalFailedTransactions(response.total);
      return response.data;
    } catch (err) {
      showErrorMessage(
        getResponseMessage(err) || 'Failed to fetch failed transactions',
      );
    }
  }

  const memoedValue = useMemo(
    () => ({
      totalPendingTransactions,
      totalFailedTransactions,
      fetchPendingTransactions,
      fetchFailedTransactions,
      menuSuffixes,
    }),

    [menuSuffixes],
  );

  return (
    <TransactionContext.Provider value={memoedValue}>
      {children}
    </TransactionContext.Provider>
  );
}
